import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import './blogPost.css';
import { BlogType } from '../../blogsData';

type Status = 'loading' | 'ready' | 'error';

interface BlogPostProps {
  blog: BlogType;
  onClose: () => void;
}

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const BlogPost: React.FC<BlogPostProps> = ({ blog, onClose }) => {
  const [markdown, setMarkdown] = useState<string>('');
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fetch(`/blogs/${blog.slug}.md`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(text => {
        if (cancelled) return;
        setMarkdown(text);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [blog.slug]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="blog-post-shell">
      <div className="blog-post">
        <div className="blog-post__topbar">
          <button className="blog-post__back" onClick={onClose} aria-label="Back to writing">
            ← Writing
          </button>
        </div>

        <article className="blog-post__article">
          <header className="blog-post__header">
            <time className="blog-post__date">{formatDate(blog.date)}</time>
            <h1 className="blog-post__title">{blog.title}</h1>
            <p className="blog-post__description">{blog.description}</p>
          </header>

          <div className="blog-post__body">
            {status === 'loading' && <p className="blog-post__status">Loading…</p>}
            {status === 'error' && (
              <p className="blog-post__status">
                Couldn't load this post. Check that <code>public/blogs/{blog.slug}.md</code> exists.
              </p>
            )}
            {status === 'ready' && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  // Responsive <picture> for blog images.
                  // Markdown referencing `/foo.webp` becomes:
                  //   <picture>
                  //     <source media="(max-width: 768px)" srcSet="/foo-mobile.webp" />
                  //     <img src="/foo.webp" alt="..." />
                  //   </picture>
                  // The mobile-only WebP is served on phones; desktop gets the full one.
                  img: ({ src, alt }) => {
                    const desktop = typeof src === 'string' ? src : '';
                    const mobile = desktop.replace(/\.webp$/, '-mobile.webp');
                    const hasMobile = desktop.endsWith('.webp') && mobile !== desktop;
                    return (
                      <picture>
                        {hasMobile && (
                          <source
                            media="(max-width: 768px)"
                            srcSet={mobile}
                            type="image/webp"
                          />
                        )}
                        <img src={desktop} alt={alt || ''} loading="lazy" decoding="async" />
                      </picture>
                    );
                  },
                }}
              >
                {markdown}
              </ReactMarkdown>
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
